/**
 * Completed Disclosures Page
 * Historical submissions and archived cycles
 */
import React, { useState } from 'react';
import { 
  CheckCircle2, 
  Download, 
  Eye, 
  Calendar, 
  FileText,
  Search,
  Filter,
  Archive,
  ExternalLink,
  Clock
} from 'lucide-react';

const COMPLETED_CYCLES = [
  {
    id: 'fy2023-csrd',
    name: 'FY2023 CSRD/ESRS',
    framework: 'CSRD',
    completedDate: '2024-06-28',
    submittedBy: 'Sarah Chen',
    status: 'submitted',
    score: 'A',
    documents: 12,
  },
  {
    id: 'fy2023-tcfd',
    name: 'FY2023 TCFD Report',
    framework: 'TCFD',
    completedDate: '2024-04-10',
    submittedBy: 'Michael Park',
    status: 'submitted',
    score: 'B+',
    documents: 8,
  },
  {
    id: '2024-cdp',
    name: '2024 CDP Climate',
    framework: 'CDP',
    completedDate: '2024-07-29',
    submittedBy: 'Emma Wilson',
    status: 'submitted',
    score: 'A-',
    documents: 15,
  },
  {
    id: 'fy2022-csrd',
    name: 'FY2022 CSRD/ESRS',
    framework: 'CSRD',
    completedDate: '2023-06-30',
    submittedBy: 'James Lee',
    status: 'archived',
    score: 'B',
    documents: 10,
  },
];

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  archived: { label: 'Archived', bg: 'bg-[#2F2F2F]', text: 'text-[#6B6B6B]' },
  audited: { label: 'Audited', bg: 'bg-blue-500/20', text: 'text-blue-400' },
};

const SCORE_COLORS = {
  'A+': 'text-emerald-400',
  'A': 'text-emerald-400',
  'A-': 'text-emerald-400',
  'B+': 'text-blue-400',
  'B': 'text-blue-400',
  'B-': 'text-blue-400',
  'C+': 'text-amber-400',
  'C': 'text-amber-400',
  'C-': 'text-amber-400',
};

export default function CompletedDisclosuresPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterYear, setFilterYear] = useState('all');

  const filteredCycles = COMPLETED_CYCLES.filter(cycle => {
    const matchesSearch = cycle.name.toLowerCase().includes(searchQuery.toLowerCase());
    const year = new Date(cycle.completedDate).getFullYear().toString();
    const matchesYear = filterYear === 'all' || year === filterYear;
    return matchesSearch && matchesYear;
  });

  const years = [...new Set(COMPLETED_CYCLES.map(c => new Date(c.completedDate).getFullYear().toString()))].sort().reverse();

  return (
    <div className="space-y-6" data-testid="completed-disclosures-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Completed Disclosures</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Historical submissions and archived disclosure cycles
          </p>
        </div>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] rounded-lg transition-colors"
          data-testid="export-all-btn"
        >
          <Download size={16} />
          <span className="text-sm font-medium">Export All</span>
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle2 size={18} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{COMPLETED_CYCLES.length}</div>
              <div className="text-xs text-[#6B6B6B]">Total Completed</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <FileText size={18} className="text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {COMPLETED_CYCLES.reduce((sum, c) => sum + c.documents, 0)}
              </div>
              <div className="text-xs text-[#6B6B6B]">Documents</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Archive size={18} className="text-purple-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">
                {COMPLETED_CYCLES.filter(c => c.status === 'archived').length}
              </div>
              <div className="text-xs text-[#6B6B6B]">Archived</div>
            </div>
          </div>
        </div>
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Clock size={18} className="text-amber-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-white">{years.length}</div>
              <div className="text-xs text-[#6B6B6B]">Years of History</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
          <input
            type="text"
            placeholder="Search completed cycles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
            data-testid="completed-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#6B6B6B]" />
          <select
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="px-3 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white focus:border-emerald-500/50 focus:outline-none"
            data-testid="completed-filter-year"
          >
            <option value="all">All Years</option>
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F]">
              <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Disclosure</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Framework</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Completed</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Submitted By</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Score</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Status</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-[#6B6B6B] uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            {filteredCycles.map(cycle => {
              const statusConfig = STATUS_CONFIG[cycle.status];
              return (
                <tr key={cycle.id} className="hover:bg-[#151515] transition-colors" data-testid={`completed-row-${cycle.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <CheckCircle2 size={14} className="text-emerald-400" />
                      </div>
                      <span className="text-sm font-medium text-white">{cycle.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs font-medium bg-[#1F1F1F] text-[#A0A0A0] rounded">
                      {cycle.framework}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#A0A0A0]">
                    {new Date(cycle.completedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#A0A0A0]">{cycle.submittedBy}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-lg font-bold ${SCORE_COLORS[cycle.score] || 'text-white'}`}>
                      {cycle.score}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}>
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="p-2 text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
                        <Eye size={14} />
                      </button>
                      <button className="p-2 text-[#6B6B6B] hover:text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-colors">
                        <Download size={14} />
                      </button>
                      <button className="p-2 text-[#6B6B6B] hover:text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors">
                        <ExternalLink size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredCycles.length === 0 && (
        <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <Archive size={48} className="mx-auto text-[#2F2F2F] mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No completed disclosures found</h3>
          <p className="text-sm text-[#6B6B6B]">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
}
